/**
 * Review Submission Form Component
 * Allows clients to submit reviews for contractors
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Rating,
  Alert,
  Grid,
  Avatar,
  Chip,
  LinearProgress
} from '@mui/material';
import { Star, Send, Close } from '@mui/icons-material';
import reviewService from '../../services/reviewService';
import DialogCloseButton from '../common/DialogCloseButton';

const ReviewSubmissionForm = ({
  open,
  onClose,
  contractorData,    // For backward compatibility
  revieweeData,      // New: supports any role
  revieweeRole,      // 'contractor', 'verifier', 'quality_inspector'
  projectData,
  verificationRequestData,
  escrowAccountData,
  milestoneData,     // For per-milestone reviews
  clientData,
  onReviewSubmitted
}) => {
  // Support both old and new prop names
  const reviewee = revieweeData || contractorData;
  const role = revieweeRole || 'contractor';

  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [professionalismRating, setProfessionalismRating] = useState(0);
  const [qualityRating, setQualityRating] = useState(0);
  const [timelinessRating, setTimelinessRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [accuracyRating, setAccuracyRating] = useState(0);
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async () => {
    if (!rating) {
      setError('Please select an overall rating');
      return;
    }

    if (!title.trim()) {
      setError('Please provide a title for your review');
      return;
    }

    if (!comment.trim()) {
      setError('Please write a detailed review');
      return;
    }

    if (comment.trim().length < 10) {
      setError('Review must be at least 10 characters long');
      return;
    }

    if (wouldRecommend === null) {
      setError('Please indicate if you would recommend this professional');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const reviewData = {
        revieweeId: reviewee.id,
        revieweeRole: role,
        projectId: projectData?.id,
        verificationRequestId: verificationRequestData?.id,
        escrowAccountId: escrowAccountData?.id,
        milestoneId: milestoneData?.id || null,
        rating: parseInt(rating),
        title: title.trim(),
        reviewText: comment.trim(),
        professionalismRating: professionalismRating || null,
        qualityRating: qualityRating || null,
        timelinessRating: timelinessRating || null,
        communicationRating: communicationRating || null,
        accuracyRating: role === 'verifier' ? accuracyRating || null : null,
        wouldRecommend: wouldRecommend
      };

      const result = await reviewService.submitReview(reviewData);

      if (result.success) {
        setSuccess('Review submitted successfully!');
        
        // Clear form
        setRating(0);
        setTitle('');
        setComment('');
        setProfessionalismRating(0);
        setQualityRating(0);
        setTimelinessRating(0);
        setCommunicationRating(0);
        setAccuracyRating(0);
        setWouldRecommend(null);
        
        // Notify parent component
        if (onReviewSubmitted) {
          onReviewSubmitted(result.data);
        }
        
        // Close dialog after delay
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      setError(error.message || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setRating(0);
      setTitle('');
      setComment('');
      setProfessionalismRating(0);
      setQualityRating(0);
      setTimelinessRating(0);
      setCommunicationRating(0);
      setAccuracyRating(0);
      setWouldRecommend(null);
      setError('');
      setSuccess('');
      onClose();
    }
  };

  const getRatingLabel = (value) => {
    const labels = {
      1: 'Poor',
      2: 'Fair', 
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return labels[value] || '';
  };

  const getRoleLabel = (role) => {
    const labels = {
      'contractor': 'Contractor',
      'verifier': 'Verifier',
      'quality_inspector': 'Quality Inspector',
      'client': 'Client'
    };
    return labels[role] || 'Professional';
  };

  if (!reviewee || !clientData) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Review {getRoleLabel(role)}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Professional Info */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1,
              mb: 2
            }}>
              <Avatar sx={{ width: 60, height: 60 }}>
                {reviewee.name?.charAt(0) || reviewee.first_name?.charAt(0)}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {reviewee.name || `${reviewee.first_name} ${reviewee.last_name}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reviewee.company_name || reviewee.businessInfo?.company_name || getRoleLabel(role)}
                </Typography>
                <Chip 
                  label={`Verified ${getRoleLabel(role)}`}
                  size="small" 
                  color="success" 
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Project/Service Info */}
          {projectData && (
            <Grid item xs={12}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Project: {projectData.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {projectData.description}
                </Typography>
              </Box>
            </Grid>
          )}
          
          {verificationRequestData && (
            <Grid item xs={12}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Verification Request: {verificationRequestData.property_address}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {verificationRequestData.verification_type}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Review Title *"
              placeholder={`Summarize your experience with this ${getRoleLabel(role).toLowerCase()}`}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (error && e.target.value.trim()) {
                  setError('');
                }
              }}
              variant="outlined"
              disabled={loading}
              inputProps={{ maxLength: 100 }}
              helperText={`${title.length}/100 characters`}
            />
          </Grid>

          {/* Overall Rating Section */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Overall Rating *
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Rating
                  name="overall-rating"
                  value={rating}
                  onChange={(event, newValue) => {
                    setRating(newValue);
                    setError('');
                  }}
                  size="large"
                  icon={<Star fontSize="inherit" />}
                  emptyIcon={<Star fontSize="inherit" />}
                />
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {rating > 0 && `${rating}/5 - ${getRatingLabel(rating)}`}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Detailed Ratings */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Detailed Ratings (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Professionalism
              </Typography>
              <Rating
                value={professionalismRating}
                onChange={(e, newValue) => setProfessionalismRating(newValue)}
                disabled={loading}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Communication
              </Typography>
              <Rating
                value={communicationRating}
                onChange={(e, newValue) => setCommunicationRating(newValue)}
                disabled={loading}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {role === 'verifier' ? 'Thoroughness' : 'Quality of Work'}
              </Typography>
              <Rating
                value={qualityRating}
                onChange={(e, newValue) => setQualityRating(newValue)}
                disabled={loading}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Timeliness
              </Typography>
              <Rating
                value={timelinessRating}
                onChange={(e, newValue) => setTimelinessRating(newValue)}
                disabled={loading}
              />
            </Box>
          </Grid>

          {role === 'verifier' && (
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Accuracy
                </Typography>
                <Rating
                  value={accuracyRating}
                  onChange={(e, newValue) => setAccuracyRating(newValue)}
                  disabled={loading}
                />
              </Box>
            </Grid>
          )}

          {/* Detailed Review */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Your Experience *
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={`Tell others about your experience. What did this ${getRoleLabel(role).toLowerCase()} do well? What could be improved?`}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                if (error && e.target.value.trim().length >= 10) {
                  setError('');
                }
              }}
              variant="outlined"
              disabled={loading}
              helperText={`${comment.length}/1000 characters (minimum 10)`}
              inputProps={{ maxLength: 1000 }}
            />
          </Grid>

          {/* Recommendation */}
          <Grid item xs={12}>
            <Box sx={{ 
              p: 2, 
              border: '1px solid', 
              borderColor: 'grey.300', 
              borderRadius: 1,
              bgcolor: wouldRecommend === true ? 'success.lighter' : wouldRecommend === false ? 'error.lighter' : 'transparent'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Would you recommend this {getRoleLabel(role).toLowerCase()}? *
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={wouldRecommend === true ? 'contained' : 'outlined'}
                  color="success"
                  onClick={() => {
                    setWouldRecommend(true);
                    setError('');
                  }}
                  disabled={loading}
                >
                  Yes, Recommend
                </Button>
                <Button
                  variant={wouldRecommend === false ? 'contained' : 'outlined'}
                  color="error"
                  onClick={() => {
                    setWouldRecommend(false);
                    setError('');
                  }}
                  disabled={loading}
                >
                  No, Don't Recommend
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Guidelines */}
          <Grid item xs={12}>
            <Box sx={{ 
              p: 2, 
              bgcolor: 'info.lighter', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.light'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Review Guidelines:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                <li>Be honest and fair in your assessment</li>
                <li>Focus on the work quality and service provided</li>
                <li>Avoid personal attacks or inappropriate language</li>
                <li>Your review will help other clients make informed decisions</li>
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
              Submitting your review...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !rating || !title.trim() || !comment.trim() || comment.trim().length < 10 || wouldRecommend === null}
          variant="contained"
          startIcon={loading ? null : <Send />}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Submitting...' : 'Submit Review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewSubmissionForm;
